import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useOidc, useOidcUser, useOidcAccessToken, useOidcIdToken } from '@axa-fr/react-oidc';
import { userFound, userExpired } from '../../store/domains/auth0/oidcReducer';
import logger from '../../utils/logger';
import CryptoService from '../../utils/cUtil';
import { ensurePromise, markAuthReady, reset as resetAuthReady } from '../../services/auth/authReady';
import {
  setDekLogoutPending,
  isDekLogoutPendingActive,
} from '../../modules/logout/Logout.helpers';

const cryptoService = new CryptoService();

/**
 * Pure factory for the session-expired event handler — extracted for testability.
 *
 * reason=missing → ForgeRock cookie is absent AND Cognito still has a valid session.
 * Calling login() only goes to Cognito which silently redirects back without going
 * through Authdepot → ForgeRock, so the ForgeRock cookie is never re-established
 * and the loop repeats forever. Fix: do a full Cognito logout first so the next
 * login must go through Authdepot → ForgeRock SAML → ForgeRock cookie established.
 * The /logout page detects the dek_logout_pending flag and redirects to admissions,
 * where OidcSecure triggers a fresh full login.
 *
 * reason=invalid / undefined → ForgeRock explicitly marked the session invalid but
 * the cookie is present. login() (Cognito re-auth) triggers Authdepot → ForgeRock
 * SAML which creates a new ForgeRock session. No full logout needed.
 */
export const createSessionExpiredHandler = ({ login }) => event => {
  const isCallbackRoute = window.location.pathname.includes('logincallback');
  // Also suppress on /logout: the lazy-loaded LogoutContainer hasn't rendered yet
  // when this handler can fire (OidcReduxBridge is eager). Letting it proceed would
  // trigger a second Cognito logout redirect, creating an infinite loop.
  const isLogoutRoute = window.location.pathname.includes('/logout');
  // Suppress on /quick-app: new account creation routes. New users have no
  // ForgeRock session yet so DEK always returns 401 reason=missing here.
  // Triggering a full Cognito logout kicks the user out of the registration flow.
  // The prewarm retry logic handles eventual DEK setup after registration completes.
  const isQuickAppRoute = window.location.pathname.includes('/quick-app');
  if (isCallbackRoute || isLogoutRoute || isQuickAppRoute) {
    console.info(`[DEK] session-expired suppressed on ${window.location.pathname} — avoiding redirect loop`);
    return;
  }

  // Guard: if any DEK-logout key is still active a redirect is already in flight —
  // don't stack a second one.
  if (isDekLogoutPendingActive()) {
    console.info('[DEK] session-expired suppressed — dek_logout_pending already set');
    return;
  }

  const reason = event?.detail?.reason;
  const env = window.__ENV || {};
  const basePath = env.REACT_APP_BASE_PATH || '/application';

  if (reason === 'missing') {
    // Cognito session is still alive — login() alone won't re-establish the
    // ForgeRock cookie. Destroy the Cognito session via full logout first.
    console.info('[DEK] session-expired reason=missing — initiating full Cognito logout to force ForgeRock re-auth');
    // Set all three DEK-logout keys atomically and store the return URL.
    // Three keys so that even if 1-2 are cleared by racing OidcReduxBridge effects
    // (each effect only clears its own dedicated key), at least one survives
    // until Logout.jsx reads them.
    setDekLogoutPending(window.location.href);
    // Clear OIDC cached tokens NOW — before navigating to Cognito logout — so that
    // when /logout (lazy-loaded) renders, the OIDC library starts with isAuthenticated=false.
    // Without this, oidc.default persists into the /logout page load, the library briefly
    // sees the user as authenticated, dispatches userFound with the old id_token, tokenId
    // becomes non-null, Logout.jsx calls logout(tokenId) → logoutService redirects to
    // Cognito logout again → infinite loop.
    try { sessionStorage.removeItem('oidc.default'); } catch (e) { /* ignore */ }
    fetch(`${basePath}/logout-cleanup`, { credentials: 'include' }).catch(() => {});
    window.location.replace(
      `${env.REACT_APP_OAUTH_COGNITO_LOGOUT}?client_id=${env.REACT_APP_OAUTH_CLIENT_ID}&logout_uri=${env.REACT_APP_AUTHDEPOT_LOGOUT}?callback=${env.REACT_APP_LOGOUT_URL}`,
    );
    return;
  }

  // reason=invalid or no reason: Cognito/ForgeRock session still usable.
  // Clean up DEK cookies then trigger OIDC re-auth.
  console.info(`[DEK] session-expired received reason=${reason} — clearing DEK cookies then triggering re-login`);
  fetch(`${basePath}/logout-cleanup`, { credentials: 'include' }).catch(() => {});
  login();
};

/**
 * Component that synchronizes OIDC state with Redux
 * and triggers necessary data loading after authentication
 */
const OidcReduxBridge = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, login } = useOidc();
  const { oidcUser } = useOidcUser();
  const { accessToken } = useOidcAccessToken();
  const { idToken } = useOidcIdToken();

  // redux OIDC state to check if we need to update
  const reduxOidcState = useSelector(state => state.oidc);

  // Start in pending mode so early API calls can wait for token readiness.
  useEffect(() => {
    ensurePromise();
  }, []);

  // When the DEK detects the ForgeRock session is invalid (expired/logged out elsewhere),
  // trigger re-authentication immediately instead of leaving the user on a spinning page.
  // Exception: suppress on /logincallback — that route is the account creation landing and
  // calling login() there creates an infinite redirect loop. ForgeRock session propagation
  // after registration can be delayed; the prewarm retry logic handles recovery there.
  useEffect(() => {
    const handleSessionExpired = createSessionExpiredHandler({ login });
    window.addEventListener('dek:session-expired', handleSessionExpired);
    return () => window.removeEventListener('dek:session-expired', handleSessionExpired);
  }, [login]);

  // Sync OIDC user data to redux store
  useEffect(() => {
    if (isAuthenticated && oidcUser) {
      try {
        // Get complete token data from axa-fr/react-oidc session storage
        const oidcStorageData = JSON.parse(sessionStorage.getItem('oidc.default') || '{}');
        const tokens = oidcStorageData.tokens || {};

        // Format user data to match legacy structure
        const reduxUser = {
          id_token: tokens.idToken || idToken,
          access_token: tokens.accessToken || accessToken,
          refresh_token: tokens.refreshToken || '',
          token_type: tokens.tokenType || 'Bearer',
          scope: tokens.accessTokenPayload?.scope || '',
          profile: {
            sub: oidcUser.sub || tokens.idTokenPayload?.sub || '',
            'cognito:groups': tokens.idTokenPayload?.['cognito:groups'] || [],
            email_verified: tokens.idTokenPayload?.email_verified?.toString() || 'false',
            'cognito:username': tokens.idTokenPayload?.['cognito:username'] || '',
            identities: tokens.idTokenPayload?.identities || [],
            token_use: tokens.idTokenPayload?.token_use || 'id',
            'custom:STUDENT_PERSON_ID': oidcUser['custom:STUDENT_PERSON_ID']
              || tokens.idTokenPayload?.['custom:STUDENT_PERSON_ID'] || '',
            auth_time: tokens.idTokenPayload?.auth_time || Math.floor(Date.now() / 1000),
            email: tokens.idTokenPayload?.email || '',
            username: tokens.idTokenPayload?.['cognito:username']
              || tokens.accessTokenPayload?.username || '',
          },
          expires_at: tokens.expiresAt
              || (Math.floor(Date.now() / 1000) + (tokens.expiresIn || 3600)),
        };

        dispatch(userFound(reduxUser));
        // Auth has been initialized and Redux has the user - mark ready
        try {
          markAuthReady();
        } catch (e) {
          // noop
        }
        // Don't prewarm on /logout — the OIDC library may still have cached tokens
        // briefly (before detecting Cognito session is gone), causing a DEK call that
        // fails and fires dek:session-expired, re-triggering the logout loop.
        if (!window.location.pathname.includes('/logout')) {
          cryptoService.prewarmSessionKey();
        }
      } catch (error) {
        logger.error('Error reading OIDC session data:', error);

        // Fall back to basic user info if session storage fails
        const reduxUser = {
          id_token: idToken,
          access_token: accessToken,
          token_type: 'Bearer',
          profile: {
            ...oidcUser,
            'custom:STUDENT_PERSON_ID': oidcUser['custom:STUDENT_PERSON_ID'] || '',
          },
        };
        dispatch(userFound(reduxUser));
        markAuthReady();
        if (!window.location.pathname.includes('/logout')) {
          cryptoService.prewarmSessionKey();
        }
      }
    } else if (!isAuthenticated) {
      dispatch(userExpired());
      try {
        resetAuthReady();
      } catch (e) {
        // noop
      }
      try {
        sessionStorage.removeItem('personId');
      } catch (e) { /* ignore storage errors */ }
    }
  }, [isAuthenticated, oidcUser, accessToken, dispatch]);

  useEffect(() => {
    const personId = oidcUser?.['custom:STUDENT_PERSON_ID']
      || reduxOidcState?.user?.profile?.['custom:STUDENT_PERSON_ID'];

    if (personId) {
      sessionStorage.setItem('personId', personId);
    }
  }, [oidcUser, reduxOidcState?.user, dispatch]);

  return null;
};

export default OidcReduxBridge;
